import styled from '@emotion/styled';
import { CellDiv, Row } from '../components/Row';
import { useTableContext } from '../providers/TableProvider';
import type { VirtualItem } from '@tanstack/react-virtual';
import { colors } from 'styles/theme/colors';
import { VirtualRowItem } from '../hooks';
import type { Row as RTRow } from '@tanstack/react-table';
import { memo, useMemo } from 'react';
import { styledTheme } from 'styles/theme/theme';
import {
  isCellEditable,
  getTotalHeadersSize,
  isTaskRowUnreadForRow,
  readInvalidFlagFromOriginal,
  resolveTableRowCellBackgroundColor,
} from '../utils';
import type { TableRowBackgroundContext, TableVariant } from '../types';
import { useUser } from 'features/auth';

export type TableBodyProps<T> = {
  virtualItems: VirtualItem[];
  totalSize: number;
  rowHeight: number;
  virtualRows?: VirtualRowItem<T>[];
  renderExpandedComponent?: (row?: RTRow<T>) => React.ReactNode;
};

// Memoized expanded row component to prevent unnecessary re-renders
const ExpandedRowComponent = memo<{
  expandedRow: { isExpandedRow: true; parentRow: RTRow<unknown> };
  virtualItem: VirtualItem;
  renderExpandedComponent?: (row?: RTRow<unknown>) => React.ReactNode;
  config: Record<string, unknown>;
  variant: TableVariant;
  expandedRowBackgroundColor?: string;
  getRowBackground?: (ctx: TableRowBackgroundContext<unknown>) => string | undefined;
  loggedInUserId?: string | number;
  getInvalidRow?: (cell?: unknown) => boolean;
}>(
  ({
    expandedRow,
    virtualItem,
    renderExpandedComponent,
    config,
    variant,
    expandedRowBackgroundColor,
    getRowBackground,
    loggedInUserId,
    getInvalidRow,
  }) => {
    const columnSelector = expandedRow.parentRow
      ?.getVisibleCells()
      .find(item => item.column.id === 'Selector');
    const isFrozen = columnSelector?.column?.getIsPinned();
    const left = isFrozen ? columnSelector?.column?.getStart('left') : 0;
    const isEditable = isCellEditable(
      columnSelector?.column?.columnDef.meta?.editable,
      columnSelector
    );
    const border = columnSelector?.column?.columnDef.border;
    const cellClick = columnSelector?.column?.columnDef.meta?.onCellClick;
    const isSelector = columnSelector?.column?.id === 'Selector';

    const expandedFillerBg = useMemo(
      () =>
        resolveTableRowCellBackgroundColor({
          row: expandedRow.parentRow,
          isSelected: expandedRow.parentRow.getIsSelected(),
          isTaskUnread: isTaskRowUnreadForRow(expandedRow.parentRow, loggedInUserId),
          isInvalidRow: Boolean(
            readInvalidFlagFromOriginal(expandedRow.parentRow.original) ??
              getInvalidRow?.(expandedRow.parentRow.original)
          ),
          variant,
          themeBgPrimary: styledTheme.palette.bg.primary,
          expandedRowBackgroundColor,
          getRowBackground,
        }),
      [
        expandedRow.parentRow,
        loggedInUserId,
        getInvalidRow,
        variant,
        expandedRowBackgroundColor,
        getRowBackground,
      ]
    );

    const expandedPanelBg =
      expandedFillerBg ??
      (variant === 'transparent' ? 'transparent' : styledTheme.palette.bg.primary);

    return (
      <ExpandedRowWrapper
        key={`${expandedRow.parentRow.id}-expanded`}
        style={{
          transform: `translateY(${virtualItem.start}px)`,
          height: `${virtualItem.size}px`,
        }}
      >
        <CellDiv
          colSpan={1}
          key={columnSelector?.id}
          className={`${isFrozen ? 'frozen ' : ''}${isSelector ? 'selector ' : ''}`}
          isEditable={isEditable}
          noPadding={columnSelector?.column.columnDef.meta?.noPadding}
          bold={columnSelector?.column.columnDef.meta?.bold}
          onClick={() => {
            if (!isSelector && cellClick && !isEditable) {
              cellClick(columnSelector);
            }
          }}
          style={{
            left,
            width: columnSelector?.column?.getSize(),
            minWidth: columnSelector?.column?.getSize(),
            borderRight: `0.5px solid ${styledTheme.palette.border.primarySubtle}`,
            height: '100%',
            backgroundColor: expandedPanelBg,
            ...(border === 'left' && {
              borderLeft: `0.5px solid ${styledTheme.palette.border.primarySubtle}`,
            }),
            ...(border === 'right' && {
              borderRight: `0.5px solid ${styledTheme.palette.border.primarySubtle}`,
            }),
            ...(border === 'both' && {
              borderLeft: `0.5px solid ${styledTheme.palette.border.primarySubtle}`,
              borderRight: `0.5px solid ${styledTheme.palette.border.primarySubtle}`,
            }),
            zIndex: 5,
          }}
        >
          <div
            style={{
              zIndex: 5,
              height: '100%',
              width: '100%',
              backgroundColor: expandedPanelBg,
            }}
          />
        </CellDiv>
        <ExpandedCell
          style={{ height: '100%', backgroundColor: expandedPanelBg }}
          colSpan={expandedRow.parentRow.getVisibleCells().length - 1}
        >
          {renderExpandedComponent?.(expandedRow.parentRow)}
        </ExpandedCell>
      </ExpandedRowWrapper>
    );
  }
);

ExpandedRowComponent.displayName = 'ExpandedRowComponent';

export const TableBody = <T,>({
  virtualItems,
  totalSize,
  rowHeight,
  virtualRows,
  renderExpandedComponent,
}: TableBodyProps<T>) => {
  const {
    table,
    config,
    variant = 'default',
    expandedRowBackgroundColor,
    getRowBackground,
  } = useTableContext();
  const { rows } = table.getRowModel();
  const tableWidth = getTotalHeadersSize(table);
  const { data: userData } = useUser();
  const loggedInUserId = userData?.loginContext?.id;
  const getInvalidRow = table?.options?.meta?.getInvalidRow;

  return (
    <TBody
      rowHeight={rowHeight}
      style={{ height: `${totalSize}px`, position: 'relative' }}
      v1={config?.v1}
      $variant={variant}
    >
      {virtualItems.map(virtualItem => {
        const virtualRow = virtualRows?.[virtualItem.index] as RTRow<T>;
        const row = virtualRow ?? rows?.[virtualItem.index];
        const isCustomExpandedRow = virtualRow && 'isExpandedRow' in virtualRow;

        if (isCustomExpandedRow) {
          const expandedRow = virtualRow as unknown as { isExpandedRow: true; parentRow: RTRow<T> };
          return (
            <ExpandedRowComponent
              key={`${expandedRow.parentRow.id}-expanded`}
              expandedRow={expandedRow as { isExpandedRow: true; parentRow: RTRow<unknown> }}
              virtualItem={virtualItem}
              renderExpandedComponent={
                renderExpandedComponent as unknown as (r?: RTRow<unknown>) => React.ReactNode
              }
              config={config}
              variant={variant}
              expandedRowBackgroundColor={expandedRowBackgroundColor}
              getRowBackground={
                getRowBackground as
                  | ((ctx: TableRowBackgroundContext<unknown>) => string | undefined)
                  | undefined
              }
              loggedInUserId={loggedInUserId}
              getInvalidRow={getInvalidRow}
            />
          );
        }

        return (
          <Row
            rowIndex={virtualItem.index}
            key={row.id}
            row={row as unknown as RTRow<unknown>}
            virtualItem={virtualItem}
            config={config}
            tableWidth={tableWidth}
            loggedInUserId={loggedInUserId}
            getInvalidRow={getInvalidRow}
          />
        );
      })}
    </TBody>
  );
};

export const TBody = styled.tbody<{ rowHeight: number; v1?: boolean; $variant?: TableVariant }>`
  display: block;
  background-color: ${p =>
    p.$variant === 'transparent' ? 'transparent' : p.theme.palette.bg.primary};

  td {
    font-size: ${p => (p.v1 ? p.theme.typography.size.sm : p.theme.typography.size.xs)};
    padding: ${p => (p.v1 ? `${p.theme.s.sm} ${p.theme.s.lg}` : `${p.theme.s.xs} ${p.theme.s.sm}`)};
    color: ${p => p.theme.palette.text.secondary};
    background-color: ${p =>
      p.$variant === 'transparent' ? 'transparent' : p.theme.palette.bg.primary};
    height: ${p => p.rowHeight}px;
    font-weight: 400;
    white-space: nowrap;
    overflow: hidden;
    display: flex;
    align-items: center;
    > * {
      white-space: nowrap !important;
      overflow: hidden !important;
      text-overflow: ellipsis !important;
    }
    & > input,
    & > div,
    & > span {
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
      width: 100%;
    }
  }

  td button:not(.primary-buttons):not(.actions-button) {
    background: none;
    border-radius: 0px;
    height: 100%;
  }

  td input {
    height: 100%;
    border: 0;
  }

  tr {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    &:not(:first-of-type) {
      border-top: 1px solid ${p => p.theme.palette.border.tertiary};
    }

    td {
      min-height: ${p => p.rowHeight}px;
    }

    &:not(.editing-active):hover td {
      background-color: ${p => p.theme.palette.bg.primaryHover};
    }

    input {
      background-color: transparent;
    }

    td.unread {
      background-color: #f2f4ff;
    }
  }

  tr.selected {
    background-color: ${colors.mintGreen[50]};
    td,
    td.frozen,
    :nth-of-type(odd) td {
      background-color: ${colors.mintGreen[50]};
    }
  }
`;

const ExpandedRowWrapper = styled.tr`
  position: absolute;
  width: 100%;
  display: block;
  border-top: unset !important;
  width: 100%;
  z-index: 1;
`;

const ExpandedCell = styled.td`
  padding: 0 !important;
  width: 100%;
  display: block;
`;
